/**
 * MasterGanttRightRail.tsx
 * Phase 4, Item 6 — Right-rail panel for the Master Gantt page.
 *
 * Three sections:
 *   1. LEGEND           — color swatches mapped to theme.schedule.* tokens
 *   2. SUMMARY TOTALS   — computed via computeMasterGanttSummary()
 *   3. SKILL LEVEL GUIDE — L1–L5 swatches
 *
 * The panel is 280 px wide, scrollable if content overflows.
 * All colors reference theme.schedule.* — no hex literals.
 *
 * Skill level note:
 *   Phase 1 defined SkillLevel as 1|2|3|4 in the DB type. The mockup
 *   "Overall Project Schedule.png" shows 5 rows in the Skill Level Guide
 *   (Basic / Limited / Competent / Proficient / Expert). We render L1–L5
 *   display-only rows here, using theme.schedule.skillLevel5 for L5. If a
 *   5th DB level is approved later, the SkillLevel union type must be extended
 *   and a migration written.  See theme.ts comment on skillLevel5.
 */

import React from 'react';
import { Box, Divider, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import type { MasterGanttResponse } from '../../../types/schedule.js';
import { computeMasterGanttSummary } from './masterGanttSummary.js';

// ---------------------------------------------------------------------------
// Sub-component: LegendRow
// ---------------------------------------------------------------------------

interface LegendRowProps {
  /** Background color of the swatch. */
  color: string;
  /** If true, renders a diagonal-stripe pattern overlay to indicate tentative. */
  hatched?: boolean;
  /** If true, renders as a dashed vertical bar (for line markers). */
  lineStyle?: 'solid' | 'dashed';
  /** If true, renders a circular badge shape instead of a bar swatch. */
  badge?: boolean;
  /** Label text. */
  label: string;
}

const SWATCH_W = 20;
const SWATCH_H = 12;

const LegendRow: React.FC<LegendRowProps> = ({
  color,
  hatched = false,
  lineStyle,
  badge = false,
  label,
}) => {
  const isLine = lineStyle !== undefined;

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.25 }}>
      {/* Swatch */}
      <Box
        sx={{
          width: badge ? SWATCH_H : SWATCH_W,
          height: SWATCH_H,
          borderRadius: badge ? '50%' : '2px',
          flexShrink: 0,
          position: 'relative',
          overflow: 'hidden',
          // Line style: thin vertical bar with border-left
          ...(isLine
            ? {
                width: 4,
                height: SWATCH_H,
                borderLeft: `3px ${lineStyle === 'dashed' ? 'dashed' : 'solid'} ${color}`,
                bgcolor: 'transparent',
              }
            : {
                bgcolor: color,
                opacity: hatched ? 0.85 : 1,
              }),
        }}
      >
        {/* Hatching overlay for tentative bars */}
        {hatched && !isLine && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `repeating-linear-gradient(
                -45deg,
                transparent,
                transparent 3px,
                rgba(255,255,255,0.55) 3px,
                rgba(255,255,255,0.55) 5px
              )`,
            }}
          />
        )}
      </Box>

      <Typography variant="caption" sx={{ lineHeight: 1.4 }}>
        {label}
      </Typography>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Sub-component: SectionHeader
// ---------------------------------------------------------------------------

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      display: 'block',
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
);

// ---------------------------------------------------------------------------
// Sub-component: SummaryRow
// ---------------------------------------------------------------------------

interface SummaryRowProps {
  label: string;
  value: number | string;
  indent?: boolean;
}

const SummaryRow: React.FC<SummaryRowProps> = ({ label, value, indent = false }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="baseline"
    sx={{ pl: indent ? 1.5 : 0 }}
  >
    <Typography variant="body2" color={indent ? 'text.secondary' : 'text.primary'}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={indent ? 400 : 600}>
      {value}
    </Typography>
  </Stack>
);

// ---------------------------------------------------------------------------
// Sub-component: SkillRow
// ---------------------------------------------------------------------------

interface SkillRowProps {
  level: number;
  label: string;
  color: string;
}

const SkillRow: React.FC<SkillRowProps> = ({ level, label, color }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.25 }}>
    <Box
      sx={{
        width: SWATCH_W,
        height: SWATCH_H,
        borderRadius: '2px',
        bgcolor: color,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: '#fff', fontSize: '0.6rem', fontWeight: 700, lineHeight: 1 }}
      >
        {level}
      </Typography>
    </Box>
    <Typography variant="caption">{label}</Typography>
  </Stack>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/** Expanded panel width (px). */
const RAIL_WIDTH = 280;
/** Collapsed rail width (px) — just wide enough for the toggle + vertical label. */
const RAIL_COLLAPSED_WIDTH = 40;

export interface MasterGanttRightRailProps {
  /** Loaded Gantt data. May be null while the initial fetch is in progress. */
  data: MasterGanttResponse | null;
  /** Whether the rail is collapsed to a thin strip. Controlled by the parent. */
  collapsed: boolean;
  /** Toggle collapse/expand. Parent owns the state (so it can reflow the chart). */
  onToggleCollapsed: () => void;
}

export const MasterGanttRightRail: React.FC<MasterGanttRightRailProps> = ({
  data,
  collapsed,
  onToggleCollapsed,
}) => {
  const theme = useTheme();
  const s = theme.schedule;

  const summary = computeMasterGanttSummary(data);

  return (
    <Paper
      variant="outlined"
      square
      sx={{
        width: collapsed ? RAIL_COLLAPSED_WIDTH : RAIL_WIDTH,
        minWidth: collapsed ? RAIL_COLLAPSED_WIDTH : RAIL_WIDTH,
        flexShrink: 0,
        borderTop: 'none',
        borderBottom: 'none',
        borderRight: 'none',
        position: 'relative',
        overflow: 'hidden',
        // Smooth slide when hiding/showing the rail.
        transition: theme.transitions.create(['width', 'min-width'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard,
        }),
      }}
    >
      {/* Toggle — pinned top-left, always visible (not part of the faded body). */}
      <Tooltip title={collapsed ? 'Show legend' : 'Hide legend'} placement="left">
        <IconButton
          size="small"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Show legend panel' : 'Hide legend panel'}
          sx={{
            position: 'absolute',
            top: 6,
            left: 4,
            zIndex: 2,
            color: alpha(theme.palette.text.primary, 0.55),
          }}
        >
          {collapsed ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
        </IconButton>
      </Tooltip>

      {/* Collapsed affordance: a vertical "Legend" label so the strip is legible. */}
      {collapsed && (
        <Box
          sx={{
            position: 'absolute',
            top: 46,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Typography
            variant="overline"
            sx={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              letterSpacing: 1.4,
              fontWeight: 700,
              fontSize: '0.62rem',
              color: alpha(theme.palette.text.primary, 0.45),
              whiteSpace: 'nowrap',
            }}
          >
            Legend &amp; Summary
          </Typography>
        </Box>
      )}

      {/* Body — fixed width so it clips cleanly as the rail narrows; fades out. */}
      <Box
        sx={{
          width: RAIL_WIDTH,
          height: '100%',
          overflowY: 'auto',
          px: 2,
          pt: 5,
          pb: 2,
          opacity: collapsed ? 0 : 1,
          pointerEvents: collapsed ? 'none' : 'auto',
          transition: theme.transitions.create('opacity', {
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
      {/* ── Section 1: Legend ─────────────────────────────────────────────── */}
      <SectionHeader>Legend</SectionHeader>

      <LegendRow color={s.projectEnvelope}  label="Project / Work Order" />
      <LegendRow color={s.machineTime}       label="Machine Time" />
      <LegendRow
        color={s.machineTime}
        hatched
        label="Machine Time (Tentative)"
      />
      <LegendRow color={s.employeeAlloc}     label="Employee Allocation" />
      <LegendRow
        color={s.employeeAlloc}
        hatched
        label="Employee Allocation (Tentative)"
      />
      <LegendRow color={s.committed}         label="Committed Work (Booked)" />
      {/* Run Mode Phase C — continuous (24/7) machine bookings run through
          weekends; the technician is booked every working day of the run
          (lab answer Q2, 2026-07-10). */}
      <LegendRow color={s.continuous}        label="Continuous (runs 24/7 incl. weekends)" />
      <LegendRow
        color={s.tentative}
        hatched
        label="Quote / Proposal (Tentative)"
      />
      <LegendRow color={s.maintenance}             label="Maintenance" />
      <LegendRow color={alpha(s.maintenance, 0.7)} label="Calibration" />
      <LegendRow color={s.outOfService}      label="Out of Service" />
      <LegendRow color={s.conflictHard}  badge label="Hard Conflict" />
      <LegendRow color={s.conflictSoft}  badge label="Soft Conflict" />
      <LegendRow
        color={s.todayLine}
        lineStyle="solid"
        label="Today"
      />
      <LegendRow
        color={s.criticalPath}
        lineStyle="dashed"
        label="Critical Path"
      />

      <Divider sx={{ my: 1.5 }} />

      {/* ── Section 2: Summary Totals ─────────────────────────────────────── */}
      <SectionHeader>Summary</SectionHeader>

      <SummaryRow label="Total Projects"       value={summary.projects} />
      <SummaryRow label="Total Work Orders"    value={summary.wosConfirmed + summary.wosProposed} />
      <SummaryRow label="+ Confirmed"          value={summary.wosConfirmed} indent />
      <SummaryRow label="+ Proposed"           value={summary.wosProposed}  indent />
      <SummaryRow label="Total Machines"       value={summary.machines} />
      <SummaryRow label="Total Employees"      value={summary.employees} />

      {/* "All Tasks: Scheduled" — matches the mockup label verbatim */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ mt: 0.5 }}
      >
        <Typography variant="body2">All Tasks</Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" fontWeight={600}>
            {summary.tasksScheduled}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              bgcolor: theme.palette.primary.main,
              color: '#fff',
              borderRadius: '3px',
              px: 0.6,
              py: 0.1,
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
            }}
          >
            SCHEDULED
          </Typography>
        </Stack>
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      {/* ── Section 3: Skill Level Guide ───────────────────────────────────── */}
      {/*
       * BUG-2026-0124: the skill scale is now 1–5 in the DB (CK_SM_Level allows
       * 1–5; migration 077). Labels match the canonical SKILL_LEVEL_META so this
       * rail stays in sync with the Skill Matrix legend.
       */}
      <SectionHeader>Skill Level Guide</SectionHeader>

      <SkillRow level={1} label="Has not begun"  color={s.skillLevel1} />
      <SkillRow level={2} label="Training"       color={s.skillLevel2} />
      <SkillRow level={3} label="Proficient"     color={s.skillLevel3} />
      <SkillRow level={4} label="Reviewer"       color={s.skillLevel4} />
      <SkillRow level={5} label="Trainer"        color={s.skillLevel5} />
      </Box>
    </Paper>
  );
};

export default MasterGanttRightRail;
